import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import PageHeader from "@/components/base/PageHeader";
import { SkeletonBlock } from "@/components/base/Skeleton";
import { getQuote, getKline, getStocks, getStockMetrics } from "@/api/market";
import KlineChart from "@/components/feature/KlineChart";
import MinuteChart from "@/components/feature/MinuteChart";
import { listSessions, getStockSnapshot } from "@/api/alpha";

// View model consumed by the existing markup — assembled from the live
// quote + (optional) stock-list row for industry / market cap, so the
// JSX layout below stays byte-for-byte the same.
interface StockView {
  name: string;
  code: string;
  currentPrice: number;
  change: number;
  changePercent: number;
  high: number;
  low: number;
  open: number;
  prevClose: number;
  volume: string;
  amount: string;
  pe: string;
  pb: string;
  roe: string;
  marketCap: string;
  sector: string;
}

// Best-effort large-number formatter. Volume is served in 手, turnover and
// market cap in 元 (akshare/baostock convention) — folded into 万 / 亿 / 万亿
// so the compact grid cells stay readable.
function formatBig(n: number, unit = ""): string {
  if (!n || n <= 0 || !Number.isFinite(n)) return "--";
  const abs = Math.abs(n);
  if (abs >= 1e12) return (n / 1e12).toFixed(2) + "万亿" + unit;
  if (abs >= 1e8) return (n / 1e8).toFixed(2) + "亿" + unit;
  if (abs >= 1e4) return (n / 1e4).toFixed(2) + "万" + unit;
  return n.toFixed(0) + unit;
}

function fmtRatio(n: number): string {
  if (n == null || n === undefined) return "--";
  const v = Number(n);
  return v > 0 && Number.isFinite(v) ? v.toFixed(2) : "--";
}

// Market cap (totalCap/floatCap) is already served in 亿元 by the backend
// (model comment: `TotalCap float64 // 亿元`). It must NOT go through
// formatBig (which assumes 元) — that under-reports by 1e8. Fold ≥1万亿
// into 万亿, otherwise render 亿 directly.
function formatCap(price: number): string {
  if (!price || price <= 0 || !Number.isFinite(price)) return "--";
  const yiyuan = price / 100000000; // 元 → 亿元
  if (yiyuan >= 1e4) return (yiyuan / 1e4).toFixed(2) + "万亿";
  return yiyuan.toFixed(2) + "亿";
}

// Strip the canonical market prefix (sh/sz/bj) so a "sz300750" list code
// matches the bare 6-digit route param.
function stripPrefix(code: string): string {
  return code.replace(/^[a-zA-Z]+/, "");
}

export default function StockDetail() {
  const { code = "" } = useParams<{ code: string }>();
  const [chartType, setChartType] = useState<"day" | "minute">("minute");
  const [isFollowed, setIsFollowed] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [stockData, setStockData] = useState<StockView | null>(null);
  const [candleData, setCandleData] = useState<any[]>([]);
  const [minuteData, setMinuteData] = useState<any[]>([]);
  const [aiSummary, setAiSummary] = useState("");

  const loadData = useCallback(async () => {
    setError("");
    try {
      // Quote is the gating call; the rest degrade gracefully.
      // Try prefixed first (DB stores codes with sh/sz prefix), fall back to bare code
      const prefixed = code.length === 6
        ? ('6' === code[0] || '5' === code[0] ? 'sh' : 'sz') + code
        : null;
      let quote;
      try {
        const r = await getQuote(prefixed || code);
        quote = r.quote;
      } catch {
        if (prefixed) {
          const r = await getQuote(code);
          quote = r.quote;
        } else {
          throw new Error("ticker not found");
        }
      }

      // Derive change / pct from price − prevClose so we never depend on the
      // backend's changePct unit (percent vs fraction).
      const change = quote.prevClose > 0 ? quote.price - quote.prevClose : quote.changeAmt;
      const changePercent = quote.prevClose > 0 ? (change / quote.prevClose) * 100 : quote.changePct;

      // Industry + market cap come from the screener row, not the quote.
      let sector = "--";
      let marketCap = "--";
      let match: any = null;
      try {
        const { stocks } = await getStocks({ q: code, pageSize: 5 });
        match = stocks.find((s) => stripPrefix(s.code) === stripPrefix(code)) || null;
        if (match) {
          sector = match.industry || "--";
          marketCap = formatCap(match.totalCap);
        }
      } catch {
        /* optional enrichment — ignore */
      }

      setStockData({
        name: quote.name || code,
        code,
        currentPrice: quote.price,
        change,
        changePercent,
        high: quote.high,
        low: quote.low,
        open: quote.open,
        prevClose: quote.prevClose,
        volume: formatBig(quote.volume, "手"),
        amount: formatBig(quote.turnover),
        pe: fmtRatio(quote.pe > 0 ? quote.pe : match?.pe),
        pb: fmtRatio(quote.pb > 0 ? quote.pb : match?.pb),
        roe: "--",
        marketCap,
        sector,
      });

      // ROE enrichment from stock-metrics API
      try {
        const { metrics } = await getStockMetrics(prefixed || code);
        if (metrics?.roe != null) {
          setStockData((prev) => prev ? { ...prev, roe: `${metrics.roe.toFixed(1)}%` } : prev);
        }
      } catch { /* ignore */ }

      // K-line: daily OHLC candles
      try {
        const { candles } = await getKline(prefixed || code, undefined, undefined, "1d");
        setCandleData(candles.filter((c: any) => c.close > 0).slice(-120));
      } catch {
        setCandleData([]);
      }

      // Minute data for 分时 — only today's trading session
      try {
        const now = new Date();
        const marketOpen = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 9, 30, 0);
        const startTs = Math.floor(marketOpen.getTime() / 1000);
        const endTs = Math.floor(now.getTime() / 1000);
        const { candles: minCandles } = await getKline(prefixed || code, String(startTs), String(endTs), "1m");
        setMinuteData(minCandles.filter((c: any) => c.close > 0));
      } catch {
        setMinuteData([]);
      }

      // AI snapshot — needs a session. Optional.
      try {
        const { sessions } = await listSessions();
        const s = sessions.find((x) => x.status === "running") || sessions[0];
        if (s) {
          const snap = await getStockSnapshot(s.id, code);
          const text = snap.latestRound?.reasoning || snap.latestCard?.reason || "";
          setAiSummary(text);
        } else {
          setAiSummary("");
        }
      } catch {
        setAiSummary("");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "加载失败");
      setStockData(null);
    }
  }, [code]);

  useEffect(() => {
    setLoading(true);
    loadData().finally(() => setLoading(false));
  }, [loadData]);

  // ── Real-time price refresh: every 1s after initial load ──
  // Backend reads from Redis (0-1ms, written every ~1s by the ETL), so the
  // cost is negligible. Only the live fields are updated; static info stays.
  useEffect(() => {
    if (loading || error) return;
    const prefixed = code.length === 6
      ? ('6' === code[0] || '5' === code[0] ? 'sh' : 'sz') + code
      : null;
    const timer = setInterval(async () => {
      try {
        let quote;
        try {
          const r = await getQuote(prefixed || code);
          quote = r.quote;
        } catch {
          if (prefixed) {
            const r = await getQuote(code);
            quote = r.quote;
          } else { return; }
        }
        setStockData(prev => {
          if (!prev) return prev;
          const change = quote.prevClose > 0 ? quote.price - quote.prevClose : quote.changeAmt;
          const changePercent = quote.prevClose > 0 ? (change / quote.prevClose) * 100 : quote.changePct;
          return {
            ...prev,
            currentPrice: quote.price,
            change,
            changePercent,
            high: quote.high || prev.high,
            low: quote.low || prev.low,
            volume: formatBig(quote.volume, "手"),
            amount: formatBig(quote.turnover),
          };
        });
      } catch { /* keep showing the last known price */ }
    }, 10000);
    return () => clearInterval(timer);
  }, [loading, error, code]);

  const up = stockData ? stockData.change >= 0 : true;
  const priceColor = up ? "#E11D48" : "#10B981";
  const sign = up ? "+" : "";

  return (
    <div className="pb-[80px]">
      <PageHeader
        title=""
        showBack={true}
        rightAction={
          <button
            onClick={() => setIsFollowed(!isFollowed)}
            className={`w-8 h-8 rounded-full flex items-center justify-center cursor-pointer ${
              isFollowed ? "bg-accent-100 text-accent-500" : "bg-background-100 text-foreground-400"
            }`}
          >
            <i className={isFollowed ? "ri-star-fill" : "ri-star-line"} />
          </button>
        }
      />

      {/* Loading */}
      {loading && (
        <div className="px-4 pt-2 space-y-4">
          <SkeletonBlock className="h-6 w-40" />
          <SkeletonBlock className="h-10 w-56" />
          <SkeletonBlock className="h-[180px] w-full rounded-xl" />
          <SkeletonBlock className="h-28 w-full rounded-xl" />
          <SkeletonBlock className="h-24 w-full rounded-xl" />
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className="px-4 py-16 text-center">
          <p className="text-sm text-foreground-500">{error}</p>
          <button
            onClick={() => { setLoading(true); loadData().finally(() => setLoading(false)); }}
            className="mt-3 text-xs text-primary-500 font-medium cursor-pointer"
          >
            重试
          </button>
        </div>
      )}

      {!loading && !error && stockData && (
        <>
          {/* Stock Name */}
          <div className="px-4 pt-2 pb-1">
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-bold text-foreground-950">{stockData.name}</h1>
              <span className="text-sm text-foreground-400">{stockData.code}</span>
            </div>
          </div>

          {/* Price */}
          <div className="px-4 mb-3">
            <div className="flex items-baseline gap-3">
              <span className="text-3xl font-extrabold text-foreground-950">¥{stockData.currentPrice.toFixed(2)}</span>
              <span className="text-lg font-bold" style={{ color: priceColor }}>{sign}{stockData.change.toFixed(2)}</span>
              <span className="text-base font-bold" style={{ color: priceColor }}>{sign}{stockData.changePercent.toFixed(2)}%</span>
            </div>
            <div className="flex items-center gap-1 mt-1">
              <span className="text-[10px] text-accent-500 bg-accent-100/80 px-1.5 py-0.5 rounded-full font-medium">模拟</span>
            </div>
          </div>

          {/* Chart */}
          <div className="px-3 mb-3">
            <div className="bg-white rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <button
                  onClick={() => setChartType("minute")}
                  className={`text-xs px-3 py-1 rounded-full cursor-pointer ${chartType === "minute" ? "bg-primary-500 text-white" : "bg-background-100 text-foreground-500"}`}
                >
                  分时
                </button>
                <button
                  onClick={() => setChartType("day")}
                  className={`text-xs px-3 py-1 rounded-full cursor-pointer ${chartType === "day" ? "bg-primary-500 text-white" : "bg-background-100 text-foreground-500"}`}
                >
                  日K
                </button>
              </div>
              {chartType === "day" && candleData.length >= 2 && (
                <KlineChart data={candleData.map((d: any) => ({ time: d.tradeDate || d.trade_time, open: d.open, close: d.close, high: d.high, low: d.low, volume: d.volume }))} />
              )}
              {chartType === "minute" && minuteData.length >= 2 && (
                <MinuteChart data={minuteData} prevClose={stockData?.prevClose || 0} />
              )}
              {((chartType === "day" && candleData.length < 2) || (chartType === "minute" && minuteData.length < 2)) && (
                <div className="flex items-center justify-center h-[200px] bg-background-50 rounded-lg">
                  <p className="text-xs text-foreground-400">暂无 K 线数据</p>
                </div>
              )}
            </div>
          </div>
          {/* Key Metrics */}
          <div className="px-4 mb-4">
            <div className="bg-white rounded-xl p-4">
              <h3 className="text-sm font-semibold text-foreground-950 mb-3">行情数据</h3>
              <div className="grid grid-cols-3 gap-3">
                <div><p className="text-[10px] text-foreground-400">最高</p><p className="text-sm font-semibold text-[#E11D48]">¥{stockData.high.toFixed(2)}</p></div>
                <div><p className="text-[10px] text-foreground-400">最低</p><p className="text-sm font-semibold text-[#10B981]">¥{stockData.low.toFixed(2)}</p></div>
                <div><p className="text-[10px] text-foreground-400">昨收</p><p className="text-sm font-semibold text-foreground-950">¥{stockData.prevClose.toFixed(2)}</p></div>
                <div><p className="text-[10px] text-foreground-400">开盘</p><p className="text-sm font-semibold text-foreground-950">¥{stockData.open.toFixed(2)}</p></div>
                <div><p className="text-[10px] text-foreground-400">成交量</p><p className="text-sm font-semibold text-foreground-950">{stockData.volume}</p></div>
                <div><p className="text-[10px] text-foreground-400">成交额</p><p className="text-sm font-semibold text-foreground-950">{stockData.amount}</p></div>
              </div>
            </div>
          </div>

          {/* Financial Metrics */}
          <div className="px-4 mb-4">
            <div className="bg-white rounded-xl p-4">
              <h3 className="text-sm font-semibold text-foreground-950 mb-3">财务指标</h3>
              <div className="grid grid-cols-4 gap-3">
                <div className="text-center"><p className="text-[10px] text-foreground-400">PE(TTM)</p><p className="text-sm font-bold text-foreground-950">{stockData.pe}</p></div>
                <div className="text-center"><p className="text-[10px] text-foreground-400">PB</p><p className="text-sm font-bold text-foreground-950">{stockData.pb}</p></div>
                <div className="text-center"><p className="text-[10px] text-foreground-400">ROE</p><p className="text-sm font-bold text-foreground-950">{stockData.roe}</p></div>
                <div className="text-center"><p className="text-[10px] text-foreground-400">总市值</p><p className="text-sm font-bold text-foreground-950">{stockData.marketCap}</p></div>
              </div>
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-background-200">
                <span className="text-[10px] text-foreground-400">行业</span>
                <span className="text-xs text-foreground-700">{stockData.sector}</span>
              </div>
            </div>
          </div>

          {/* AI Analysis */}
          <div className="px-4 mb-4">
            <div className="bg-accent-50 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-foreground-950 mb-2">🤖 AI 分析摘要</h3>
              <p className="text-sm text-foreground-700 leading-relaxed">
                {aiSummary || "AI 暂未对该股生成研判，开启模拟盘后由智能体自动分析。"}
              </p>
              <p className="text-[10px] text-foreground-400 mt-2">模拟 · 仅供学习参考</p>
            </div>
          </div>

          <p className="text-[10px] text-foreground-400 text-center px-4">
            模拟 · 仅供学习参考，不构成投资建议
          </p>
        </>
      )}
    </div>
  );
}
